/**
 * Panneau latéral de correction d'une fiche client (conformité FNE).
 * Champs préremplis, erreurs et formats attendus, enregistrement sans quitter le tableau.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { AlertCircle, ArrowRight, Check, Loader2, ShieldOff, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from '../../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const FIELD_DEFS = {
  phone: {
    label: 'Téléphone', payloadKey: 'phone', required: true, placeholder: '0700000000',
    format: 'Format attendu : 10 chiffres commençant par 01, 05, 07, 21, 25 ou 27',
    check: (v) => (/^(01|05|07|21|25|27)\d{8}$/.test(String(v || '').replace(/\D/g, '').replace(/^(00225|225)/, ''))
      ? null : 'Numéro non conforme'),
  },
  ncc: {
    label: 'NCC', payloadKey: 'compteContribuable', required: true, placeholder: '2505262M',
    format: 'Format attendu : 7 chiffres suivis d’une lettre majuscule (ex. 2505262M)',
    check: (v) => (/^\d{7}[A-Z]$/.test(String(v || '').replace(/[\s\-.]/g, '').toUpperCase())
      ? null : 'NCC non conforme'),
  },
  email: {
    label: 'Email', payloadKey: 'email', required: true, placeholder: 'contact@societe.ci',
    format: 'Adresse électronique valide — la DGI y envoie la facture certifiée',
    check: (v) => (/^[^@\s]+@[^@\s]+\.[A-Za-z]{2,}$/.test(String(v || '').trim()) ? null : 'Email non conforme'),
  },
  rccm: {
    label: 'RCCM', payloadKey: 'rccm', required: false, placeholder: 'CI-ABJ-03-2025-B12-04146',
    format: 'Numéro d’immatriculation au registre du commerce', check: () => null,
  },
  address: {
    label: 'Adresse', payloadKey: 'address', required: false, placeholder: 'Commune, rue, repère',
    format: 'Adresse géographique complète (commune, rue, repère)', check: () => null,
  },
};

const ORDER = ['phone', 'ncc', 'email', 'rccm', 'address'];

export const ClientConformityPanel = ({ row, onClose, onSaved, onSavedAndNext, hasNext }) => {
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [serverErrors, setServerErrors] = useState({});
  const [excluded, setExcluded] = useState(false);
  const [exclusionReason, setExclusionReason] = useState('');

  useEffect(() => {
    setServerErrors({});
    setValues(Object.fromEntries(ORDER.map(k => [k, row?.fields?.[k]?.value ?? ''])));
    setExcluded(!!row?.fneExcluded);
    setExclusionReason(row?.fneExclusionReason || '');
  }, [row]);

  const initial = useMemo(
    () => Object.fromEntries(ORDER.map(k => [k, row?.fields?.[k]?.value ?? ''])),
    [row],
  );
  const exclusionChanged = !!row && (excluded !== !!row.fneExcluded);
  const dirty = ORDER.some(k => String(values[k] ?? '') !== String(initial[k] ?? '')) || exclusionChanged;

  const errorFor = (k) => {
    if (serverErrors[FIELD_DEFS[k].payloadKey]) return serverErrors[FIELD_DEFS[k].payloadKey];
    const v = String(values[k] ?? '').trim();
    if (!v) return null;
    return FIELD_DEFS[k].check(v);
  };
  const hasError = ORDER.some(k => errorFor(k));

  const close = () => {
    if (dirty && !window.confirm('Des modifications ne sont pas enregistrées. Quitter quand même ?')) return;
    onClose();
  };

  const save = async (goNext) => {
    const changed = ORDER.filter(k => String(values[k] ?? '').trim() !== String(initial[k] ?? '').trim()
      && String(values[k] ?? '').trim());
    if (!changed.length && !exclusionChanged) {
      if (goNext) { onSavedAndNext?.(); return; }
      toast({ title: 'Aucune modification à enregistrer', variant: 'destructive' });
      return;
    }
    if (excluded && exclusionChanged && exclusionReason.trim().length < 10) {
      setServerErrors({ fneExclusionReason: 'Motif obligatoire (10 caractères minimum)' });
      return;
    }
    setSaving(true);
    setServerErrors({});
    try {
      const body = Object.fromEntries(changed.map(k => [FIELD_DEFS[k].payloadKey, String(values[k]).trim()]));
      if (exclusionChanged) {
        body.fneExcluded = excluded;
        if (excluded) body.fneExclusionReason = exclusionReason.trim();
      }
      const { data } = await axios.patch(`${API}/clients/completeness/clients/${row.clientId}`, body);
      toast({
        title: `${row.companyName} · ${data.updatedFields.length} champ(s) enregistré(s)`,
        description: data.propagatedTo
          ? `Appliqué aussi aux ${data.propagatedTo} autre(s) compte(s) de ${data.companyName}.`
          : (data.isCompliant ? 'Fiche conforme pour la FNE.'
            : `${data.remaining.filter(r => r.fne_blocking).length} point(s) bloquant(s) restant(s).`),
      });
      if (goNext) onSavedAndNext?.(); else onSaved?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.errors) {
        setServerErrors(Object.fromEntries(detail.errors.map(x => [x.field, x.message])));
        toast({ title: 'Valeurs non conformes', variant: 'destructive' });
      } else {
        toast({ title: 'Échec de l’enregistrement',
          description: typeof detail === 'string' ? detail : undefined, variant: 'destructive' });
      }
    } finally { setSaving(false); }
  };

  if (!row) return null;
  const blocking = (row.issues || []).filter(i => i.fne_blocking).length;

  return (
    <aside className="fixed inset-y-0 right-0 w-full sm:w-[420px] bg-white border-l border-slate-200 shadow-2xl z-50 flex flex-col"
      data-testid="conformity-panel">
      <div className="flex items-start justify-between px-4 py-3 border-b border-slate-200">
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-400">Modifier le client</div>
          <div className="font-bold text-slate-900 truncate" data-testid="conformity-panel-title">{row.companyName}</div>
          <div className="text-[11px] text-slate-500 truncate">{row.commercialName}</div>
        </div>
        <button onClick={close} className="p-1.5 rounded hover:bg-slate-100 text-slate-500"
          data-testid="conformity-panel-close"><X className="h-4 w-4" /></button>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
        {row.anomalies > 0 && (
          <div className="flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2">
            <AlertCircle className="h-4 w-4 text-rose-600 mt-0.5 shrink-0" />
            <div className="text-[11px] text-rose-800">
              <div className="font-semibold">{row.anomalies} information(s) à corriger</div>
              {blocking > 0 && <div>{blocking} bloque(nt) la facturation FNE.</div>}
            </div>
          </div>
        )}
        {ORDER.map((k) => {
          const def = FIELD_DEFS[k];
          const status = row.fields?.[k]?.status;
          if (status === 'na') return null;
          const err = errorFor(k);
          return (
            <div key={k}>
              <label className="text-xs font-semibold text-slate-700">
                {def.label}{def.required && <span className="text-rose-600"> *</span>}
              </label>
              <Input
                value={values[k] ?? ''}
                onChange={(e) => setValues(v => ({ ...v, [k]: e.target.value }))}
                placeholder={def.placeholder}
                className={`mt-1 h-9 text-sm ${err ? 'border-rose-400 focus-visible:ring-rose-200' : ''}`}
                data-testid={`conformity-panel-input-${k}`}
              />
              {err ? (
                <div className="text-[11px] text-rose-600 mt-1">{err} · {def.format}</div>
              ) : (
                <div className="text-[11px] text-slate-400 mt-1">{def.format}</div>
              )}
            </div>
          );
        })}
      </div>

      {/* Exclusion du périmètre FNE */}
      <div className="px-4 py-3 border-t border-slate-200 bg-slate-50">
        <label className="flex items-start gap-2 cursor-pointer">
          <input type="checkbox" checked={excluded}
            onChange={(e) => setExcluded(e.target.checked)}
            className="mt-0.5 h-4 w-4 accent-slate-700"
            data-testid="conformity-panel-exclude" />
          <span>
            <span className="text-xs font-semibold text-slate-800 flex items-center gap-1">
              <ShieldOff className="h-3.5 w-3.5" /> Exclure du périmètre FNE
            </span>
            <span className="block text-[11px] text-slate-500">
              Le client n'apparaîtra plus comme bloquant pour la certification et ses factures
              ne seront pas transmises à la DGI.
            </span>
          </span>
        </label>
        {excluded && (
          <div className="mt-2">
            <Input value={exclusionReason} onChange={(e) => setExclusionReason(e.target.value)}
              placeholder="Motif de l'exclusion (10 caractères minimum)"
              className={`h-8 text-xs ${serverErrors.fneExclusionReason ? 'border-rose-400' : ''}`}
              data-testid="conformity-panel-exclusion-reason" />
            <div className={`text-[10px] mt-0.5 ${serverErrors.fneExclusionReason ? 'text-rose-600' : 'text-slate-400'}`}>
              {serverErrors.fneExclusionReason || `${exclusionReason.trim().length}/10 — conservé dans l'audit`}
            </div>
          </div>
        )}
      </div>

      <div className="px-4 py-3 border-t border-slate-200 flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={close} disabled={saving} className="h-9">Annuler</Button>
        <Button size="sm" onClick={() => save(false)} disabled={saving || hasError}
          className="h-9 bg-blue-600 hover:bg-blue-700 text-white" data-testid="conformity-panel-save">
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Check className="h-3.5 w-3.5 mr-1" />}
          Enregistrer
        </Button>
        {hasNext && (
          <Button size="sm" onClick={() => save(true)} disabled={saving || hasError}
            className="h-9 bg-slate-900 hover:bg-slate-800 text-white ml-auto"
            data-testid="conformity-panel-save-next">
            Enregistrer et suivant <ArrowRight className="h-3.5 w-3.5 ml-1" />
          </Button>
        )}
      </div>
    </aside>
  );
};

export default ClientConformityPanel;
